import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Loading,
  SearchInput,
  ToastNotifications,
} from 'spark-core-dx/components';
import { useCorePermissions, useGenericManager } from 'spark-core-dx/hooks';
import ConvokeCommandBar from '../../components/convoke/ConvokeCommandBar';
import DialogControl from '../../components/convoke/ConvokeDialogControl';
import {
  buildClientMarketplacePaymentRequest,
  CLIENT_MARKETPLACE_PAYMENT_ITEM_NAME,
  CLIENT_MARKETPLACE_PAYMENT_OPERATIONS,
  runClientMarketplacePaymentOperation,
} from './clientMarketplacePaymentContracts';
import { CLIENT_COMMAND_OPERATION_PERMISSIONS } from './clientCommandPermissions';

const read = (item, ...keys) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) return item[key];
  }
  return undefined;
};

const paymentRows = (workspace) => {
  const value = read(workspace, 'PendingPayments', 'pendingPayments');
  return Array.isArray(value) ? value : [];
};

const paymentId = (payment) => String(read(
  payment,
  'ClientProductOfferId',
  'clientProductOfferId',
) || '').trim();

const paymentRequester = (payment) => read(
  payment,
  'RequestedByDisplayName',
  'requestedByDisplayName',
) || read(payment, 'RequestedByEmail', 'requestedByEmail')
  || read(payment, 'RequestedByProfileId', 'requestedByProfileId')
  || 'Requester unavailable';

const formatDateTime = (value) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const Fact = ({ label, value }) => value ? (
  <div className="command-marketplace-payments__fact">
    <dt>{label}</dt>
    <dd>{value}</dd>
  </div>
) : null;

const ClientMarketplacePaymentsScreen = ({
  clientId,
  currentEnvironment,
}) => {
  const manager = useGenericManager(CLIENT_MARKETPLACE_PAYMENT_ITEM_NAME);
  const { canExecute } = useCorePermissions();
  const managerRef = useRef(manager);
  const toastTimerRef = useRef(null);
  const [workspace, setWorkspace] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  managerRef.current = manager;
  const canActivate = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.activateMarketplacePayment,
  );
  const canDelete = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.deleteMarketplacePayment,
  );

  useEffect(() => () => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
  }, []);

  const showToast = useCallback((message) => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    setToastMessage(message);
    toastTimerRef.current = window.setTimeout(() => {
      setToastMessage('');
      toastTimerRef.current = null;
    }, 4500);
  }, []);

  const loadPayments = useCallback(async (query = '') => {
    setLoading(true);
    setLoadError('');
    try {
      const loaded = await runClientMarketplacePaymentOperation(
        managerRef.current,
        CLIENT_MARKETPLACE_PAYMENT_OPERATIONS.load,
        buildClientMarketplacePaymentRequest({
          clientId,
          currentEnvironment,
          searchText: query,
        }),
        'Pending Marketplace payments are unavailable.',
      );
      if (!loaded.workspace) {
        throw new Error('The Marketplace payment workspace was not returned.');
      }
      setWorkspace(loaded.workspace);
      setSelectedPayment((current) => {
        if (!current) return null;
        return paymentRows(loaded.workspace).find(
          (payment) => paymentId(payment) === paymentId(current),
        ) || null;
      });
    } catch (exception) {
      setLoadError(
        exception?.message || 'Pending Marketplace payments are unavailable.',
      );
    } finally {
      setLoading(false);
    }
  }, [clientId, currentEnvironment]);

  useEffect(() => {
    setSearchText('');
    setSearchDraft('');
    setSelectedPayment(null);
    setActivateDialogOpen(false);
    setDeleteDialogOpen(false);
    void loadPayments('');
  }, [clientId, currentEnvironment, loadPayments]);

  useEffect(() => {
    if (searchDraft === searchText) return undefined;
    const timeout = window.setTimeout(() => {
      const nextSearch = String(searchDraft || '').trim();
      setSearchText(nextSearch);
      void loadPayments(nextSearch);
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [loadPayments, searchDraft, searchText]);

  const activatePayment = useCallback(async () => {
    const clientProductOfferId = paymentId(selectedPayment);
    if (!canActivate || !clientProductOfferId || busy) return false;

    setBusy(true);
    setActionError('');
    try {
      const activated = await runClientMarketplacePaymentOperation(
        managerRef.current,
        CLIENT_MARKETPLACE_PAYMENT_OPERATIONS.activate,
        buildClientMarketplacePaymentRequest({
          clientId,
          currentEnvironment,
          searchText,
          clientProductOfferId,
        }),
        'The Marketplace grant could not be activated.',
      );
      if (!activated.workspace) {
        throw new Error('The refreshed Marketplace payment workspace was not returned.');
      }
      setWorkspace(activated.workspace);
      setSelectedPayment(null);
      setActivateDialogOpen(false);
      showToast('Payment confirmed. The Marketplace grant is now active.');
      return true;
    } catch (exception) {
      setActivateDialogOpen(false);
      setActionError(
        exception?.message || 'The Marketplace grant could not be activated.',
      );
      return false;
    } finally {
      setBusy(false);
    }
  }, [
    busy,
    canActivate,
    clientId,
    currentEnvironment,
    searchText,
    selectedPayment,
    showToast,
  ]);

  const deletePayment = useCallback(async () => {
    const clientProductOfferId = paymentId(selectedPayment);
    if (!canDelete || !clientProductOfferId || busy) return false;

    setBusy(true);
    setActionError('');
    try {
      const deleted = await runClientMarketplacePaymentOperation(
        managerRef.current,
        CLIENT_MARKETPLACE_PAYMENT_OPERATIONS.delete,
        buildClientMarketplacePaymentRequest({
          clientId,
          currentEnvironment,
          searchText,
          clientProductOfferId,
        }),
        'The pending Marketplace grant could not be deleted.',
      );
      if (!deleted.workspace) {
        throw new Error('The refreshed Marketplace payment workspace was not returned.');
      }
      setWorkspace(deleted.workspace);
      setSelectedPayment(null);
      setDeleteDialogOpen(false);
      showToast('The pending Marketplace grant was deleted.');
      return true;
    } catch (exception) {
      setDeleteDialogOpen(false);
      setActionError(
        exception?.message || 'The pending Marketplace grant could not be deleted.',
      );
      return false;
    } finally {
      setBusy(false);
    }
  }, [
    busy,
    canDelete,
    clientId,
    currentEnvironment,
    searchText,
    selectedPayment,
    showToast,
  ]);

  const pendingPayments = paymentRows(workspace);
  const commandBarActions = [{
    key: 'refresh-marketplace-payments',
    label: 'Refresh',
    iconKey: 'refresh',
    loading,
    disabled: loading || busy,
    onClick: () => loadPayments(searchText),
  }];
  if (canActivate) {
    commandBarActions.push({
      key: 'activate-marketplace-payment',
      label: 'Activate Grant',
      iconKey: 'success',
      disabled: loading || busy || !selectedPayment,
      onClick: () => setActivateDialogOpen(true),
    });
  }
  if (canDelete) {
    commandBarActions.push({
      key: 'delete-marketplace-payment',
      label: 'Delete Grant',
      iconKey: 'delete',
      danger: true,
      disabled: loading || busy || !selectedPayment,
      onClick: () => setDeleteDialogOpen(true),
    });
  }

  return (
    <div className="resource-workspace-overview command-entry command-marketplace-payments">
      <ConvokeCommandBar
        actions={commandBarActions}
        ariaLabel="Marketplace Payments actions"
        className="convoke-command-bar--screen"
        useSparkButtons
      />
      <section className="resource-workspace-panel command-marketplace-payments__results">
        <div className="command-marketplace-payments__heading">
          <h2>Pending Marketplace Payments</h2>
          <p>
            Select a pending purchase request, confirm payment, then activate
            its grant or delete the request.
          </p>
        </div>
        <div className="command-marketplace-payments__search">
          <SearchInput
            value={searchDraft}
            onChange={setSearchDraft}
            placeholder="Search offer, SKU, resource, or requester..."
            disabled={loading}
          />
        </div>

        {loadError ? (
          <div className="command-marketplace-payments__state command-marketplace-payments__state--error" role="alert">
            {loadError}
          </div>
        ) : null}
        {loading ? (
          <div className="command-marketplace-payments__state">
            <Loading />
            <span>Loading pending Marketplace payments...</span>
          </div>
        ) : null}
        {!loading && !loadError && pendingPayments.length === 0 ? (
          <div className="command-marketplace-payments__state">
            No Marketplace grants are awaiting payment confirmation.
          </div>
        ) : null}
        {!loading && !loadError && pendingPayments.length > 0 ? (
          <div className="command-marketplace-payments__cards resource-workspace-record-list" role="listbox" aria-label="Pending Marketplace payments">
            {pendingPayments.map((payment) => {
              const itemId = paymentId(payment);
              const requester = paymentRequester(payment);
              const selected = itemId === paymentId(selectedPayment);
              return (
                <article
                  key={itemId}
                  className={`command-marketplace-payments__card resource-workspace-record-row${selected ? ' command-marketplace-payments__card--selected' : ''}`}
                  role="option"
                  tabIndex={0}
                  aria-selected={selected}
                  onClick={() => setSelectedPayment(payment)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    setSelectedPayment(payment);
                  }}
                >
                  <div className="command-marketplace-payments__identity">
                    <span className="resource-workspace-record-row-descriptive-value">
                      {read(payment, 'Status', 'status') || 'Pending payment'}
                    </span>
                    <strong>{read(payment, 'DisplayName', 'displayName') || 'Marketplace offer'}</strong>
                    <span>{read(payment, 'Sku', 'sku') || read(payment, 'ProductOfferKey', 'productOfferKey')}</span>
                  </div>
                  <dl className="command-marketplace-payments__facts resource-workspace-entity-object-card__facts">
                    <Fact label="Price" value={read(payment, 'PriceSummary', 'priceSummary')} />
                    <Fact label="Requested by" value={requester} />
                    <Fact label="Requested" value={formatDateTime(read(payment, 'RequestedUtc', 'requestedUtc'))} />
                    <Fact label="Resource" value={read(payment, 'RequestedProductResourceKey', 'requestedProductResourceKey') || 'Processing capacity'} />
                    <Fact label="Environment" value={read(payment, 'EnvironmentKey', 'environmentKey')} />
                  </dl>
                  <div className="command-marketplace-payments__status resource-workspace-record-row-actions">
                    <span className="resource-workspace-status-pill resource-workspace-status-pill--warning">
                      Pending
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}
      </section>

      <DialogControl
        openDialog={activateDialogOpen}
        onCancel={() => setActivateDialogOpen(false)}
        onOk={activatePayment}
        title="Confirm Payment and Activate Grant"
        okText={busy ? 'Activating...' : 'Activate Grant'}
        cancelText="Cancel"
        disableOk={busy}
        disableCancel={busy}
        fallbackErrorMessage="The Marketplace grant could not be activated."
      >
        <p>
          Continue only after payment has been received for{' '}
          <strong>{read(selectedPayment, 'DisplayName', 'displayName') || 'this Marketplace offer'}</strong>.
        </p>
        <dl className="command-marketplace-payments__dialog-details">
          <Fact label="Offer" value={read(selectedPayment, 'DisplayName', 'displayName')} />
          <Fact label="SKU" value={read(selectedPayment, 'Sku', 'sku') || read(selectedPayment, 'ProductOfferKey', 'productOfferKey')} />
          <Fact label="Price" value={read(selectedPayment, 'PriceSummary', 'priceSummary')} />
          <Fact label="Requested by" value={paymentRequester(selectedPayment)} />
          <Fact label="Requested" value={formatDateTime(read(selectedPayment, 'RequestedUtc', 'requestedUtc'))} />
          <Fact label="Resource" value={read(selectedPayment, 'RequestedProductResourceKey', 'requestedProductResourceKey') || 'Processing capacity'} />
          <Fact label="Environment" value={read(selectedPayment, 'EnvironmentKey', 'environmentKey')} />
        </dl>
        <p>
          Activating the grant immediately makes its purchased allowance or
          processing capacity available to the selected client.
        </p>
      </DialogControl>

      <DialogControl
        openDialog={deleteDialogOpen}
        onCancel={() => setDeleteDialogOpen(false)}
        onOk={deletePayment}
        title="Delete Pending Grant"
        okText={busy ? 'Deleting...' : 'Delete Grant'}
        cancelText="Cancel"
        disableOk={busy}
        disableCancel={busy}
        fallbackErrorMessage="The pending Marketplace grant could not be deleted."
      >
        <p>
          Delete the pending grant for{' '}
          <strong>{read(selectedPayment, 'DisplayName', 'displayName') || 'this Marketplace offer'}</strong>?
        </p>
        <dl className="command-marketplace-payments__dialog-details">
          <Fact label="SKU" value={read(selectedPayment, 'Sku', 'sku') || read(selectedPayment, 'ProductOfferKey', 'productOfferKey')} />
          <Fact label="Price" value={read(selectedPayment, 'PriceSummary', 'priceSummary')} />
          <Fact label="Requested by" value={paymentRequester(selectedPayment)} />
          <Fact label="Requested" value={formatDateTime(read(selectedPayment, 'RequestedUtc', 'requestedUtc'))} />
        </dl>
        <p>
          This removes the grant from the payment queue so it cannot be
          activated. Its audit history is retained.
        </p>
      </DialogControl>

      <DialogControl
        openDialog={Boolean(actionError)}
        onCancel={() => setActionError('')}
        onOk={() => setActionError('')}
        title="Marketplace Payment Error"
        okText="Close"
        hideCancel
        hideCancelButton
      >
        <p>{actionError}</p>
      </DialogControl>

      {toastMessage ? (
        <ToastNotifications position="top" message={toastMessage} />
      ) : null}
    </div>
  );
};

export default ClientMarketplacePaymentsScreen;
